import { useId, useRef, type HTMLAttributes, type KeyboardEvent, type ReactNode } from 'react';
import { cx } from '../internal/cx';
import { useControllableState } from '../internal/useControllableState';
import { VisuallyHidden } from '../VisuallyHidden/VisuallyHidden';
import styles from './Tabs.module.css';

export interface TabItem {
  id: string;
  label: ReactNode;
  /** Panel content. Only the active panel's content is rendered unless `keepMounted` is set. */
  content?: ReactNode;
  disabled?: boolean;
  /** Small count or badge after the label. */
  badge?: ReactNode;
  /** The panel contains errors (e.g. invalid form fields): shows an error indicator on the tab. */
  invalid?: boolean;
}

export interface TabsProps extends Omit<HTMLAttributes<HTMLDivElement>, 'onChange'> {
  items: TabItem[];
  /** Controlled active tab id. */
  value?: string;
  defaultValue?: string;
  onValueChange?: (id: string) => void;
  /** Accessible name of the tab list (required unless aria-labelledby is given). */
  label?: string;
  'aria-labelledby'?: string;
  /** `automatic` activates on arrow focus; `manual` requires Enter/Space. */
  activation?: 'automatic' | 'manual';
  /** Class for each panel. */
  panelClassName?: string;
  /**
   * Keep every panel's content mounted and only hide the inactive ones. Use when panels hold
   * parts of one form, so state and native validation cover all fields.
   */
  keepMounted?: boolean;
  /**
   * Size the panel area to the tallest panel so the layout (e.g. a dialog) does not change
   * height when switching tabs. Implies `keepMounted`.
   */
  equalHeight?: boolean;
}

/** WAI-ARIA Tabs: roving tabindex, Arrow keys, Home/End. */
export function Tabs({
  items,
  value,
  defaultValue,
  onValueChange,
  label,
  'aria-labelledby': labelledBy,
  activation = 'automatic',
  className,
  panelClassName,
  keepMounted = false,
  equalHeight = false,
  ...rest
}: TabsProps) {
  const baseId = useId();
  const firstEnabled = items.find((item) => !item.disabled)?.id ?? '';
  const [selected, setSelected] = useControllableState(
    value,
    defaultValue ?? firstEnabled,
    onValueChange,
  );
  const activeId = items.some((item) => item.id === selected) ? selected : firstEnabled;
  const tabRefs = useRef(new Map<string, HTMLButtonElement>());

  const tabId = (id: string) => `${baseId}-tab-${id}`;
  const panelId = (id: string) => `${baseId}-panel-${id}`;

  function focusTab(id: string) {
    tabRefs.current.get(id)?.focus();
    if (activation === 'automatic') setSelected(id);
  }

  function onKeyDown(event: KeyboardEvent<HTMLButtonElement>) {
    const enabled = items.filter((item) => !item.disabled);
    if (enabled.length === 0) return;
    const currentId = (event.target as HTMLElement).dataset.tabId;
    const index = enabled.findIndex((item) => item.id === currentId);
    let next: TabItem | undefined;
    switch (event.key) {
      case 'ArrowRight':
        next = enabled[(index + 1) % enabled.length];
        break;
      case 'ArrowLeft':
        next = enabled[(index - 1 + enabled.length) % enabled.length];
        break;
      case 'Home':
        next = enabled[0];
        break;
      case 'End':
        next = enabled[enabled.length - 1];
        break;
      default:
        return;
    }
    event.preventDefault();
    if (next) focusTab(next.id);
  }

  return (
    <div className={cx(styles.tabs, className)} {...rest}>
      <div
        role="tablist"
        aria-label={label}
        aria-labelledby={labelledBy}
        aria-orientation="horizontal"
        className={styles.list}
      >
        {items.map((item) => {
          const isActive = item.id === activeId;
          return (
            <button
              key={item.id}
              ref={(el) => {
                if (el) tabRefs.current.set(item.id, el);
                else tabRefs.current.delete(item.id);
              }}
              type="button"
              role="tab"
              id={tabId(item.id)}
              data-tab-id={item.id}
              aria-selected={isActive}
              aria-controls={panelId(item.id)}
              aria-disabled={item.disabled || undefined}
              tabIndex={isActive ? 0 : -1}
              className={cx(styles.tab, isActive && styles.active, item.invalid && styles.invalid)}
              onKeyDown={onKeyDown}
              onClick={() => {
                if (!item.disabled) setSelected(item.id);
              }}
            >
              <span>
                {item.label}
                {item.invalid && (
                  <>
                    {' '}
                    <VisuallyHidden>(has errors)</VisuallyHidden>
                  </>
                )}
              </span>
              {item.badge != null && <span className={styles.badge}>{item.badge}</span>}
              {item.invalid && <span className={styles.errorDot} aria-hidden="true" />}
            </button>
          );
        })}
      </div>
      {equalHeight ? (
        // Every panel shares one grid cell; inactive ones stay in layout (invisible and inert),
        // so the area is always as tall as the tallest panel.
        <div className={styles.panelStack}>
          {items.map((item) => {
            const isActive = item.id === activeId;
            return (
              <div
                key={item.id}
                role="tabpanel"
                id={panelId(item.id)}
                aria-labelledby={tabId(item.id)}
                aria-hidden={!isActive || undefined}
                inert={!isActive}
                tabIndex={isActive ? 0 : -1}
                style={isActive ? undefined : { visibility: 'hidden' }}
                className={cx(styles.panel, panelClassName)}
              >
                {item.content}
              </div>
            );
          })}
        </div>
      ) : (
        items.map((item) => (
          <div
            key={item.id}
            role="tabpanel"
            id={panelId(item.id)}
            aria-labelledby={tabId(item.id)}
            hidden={item.id !== activeId}
            tabIndex={0}
            className={cx(styles.panel, panelClassName)}
          >
            {keepMounted || item.id === activeId ? item.content : null}
          </div>
        ))
      )}
    </div>
  );
}

export interface TabNavItem {
  id: string;
  label: ReactNode;
  href: string;
  badge?: ReactNode;
}

export interface TabNavProps extends HTMLAttributes<HTMLElement> {
  items: TabNavItem[];
  activeId: string;
  /** Accessible name for the navigation landmark. */
  label: string;
  /** Inject the router's link component. Defaults to <a>. */
  renderLink?: (
    item: TabNavItem,
    props: { className: string; 'aria-current'?: 'page'; children: ReactNode },
  ) => ReactNode;
}

/**
 * Tab-styled navigation between routes (sub-pages). Uses links + aria-current, not the ARIA
 * tabs pattern, because each tab is a separate URL.
 */
export function TabNav({ items, activeId, label, renderLink, className, ...rest }: TabNavProps) {
  return (
    <nav aria-label={label} className={cx(styles.tabs, className)} {...rest}>
      <ul className={styles.list}>
        {items.map((item) => {
          const isActive = item.id === activeId;
          const children = (
            <>
              <span>{item.label}</span>
              {item.badge != null && <span className={styles.badge}>{item.badge}</span>}
            </>
          );
          const linkProps = {
            className: cx(styles.tab, isActive && styles.active),
            ...(isActive ? { 'aria-current': 'page' as const } : {}),
            children,
          };
          return (
            <li key={item.id} className={styles.navItem}>
              {renderLink ? (
                renderLink(item, linkProps)
              ) : (
                <a
                  href={item.href}
                  className={linkProps.className}
                  aria-current={isActive ? 'page' : undefined}
                >
                  {children}
                </a>
              )}
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
